import React, { useEffect, useMemo, useState } from 'react';
import {
  FiActivity, FiAlertTriangle, FiBarChart2, FiClock, FiCloud, FiDollarSign, FiTarget, FiTrendingUp, FiUsers,
} from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const GRANULARITY_ROWS = [
  ['daily', 'Today'], ['weekly', 'This week'], ['monthly', 'This month'],
  ['quarterly', 'This quarter'], ['yearly', 'This year'],
];

const StatCard = ({ icon, label, value, sub, color }) => (
  <div style={fs.statCard}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: color || fs.colors.textDim, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {icon}{label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 700, color: fs.colors.textBright, marginTop: 8 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: fs.colors.textFaint, marginTop: 4 }}>{sub}</div>}
  </div>
);

const percent = (v) => {
  if (v === null || v === undefined) return '—';
  return `${(parseFloat(v) * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

const CFOMetricsDashboard = () => {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [periods, setPeriods] = useState([]);
  const [periodName, setPeriodName] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    financeAPI.entities.list().then((res) => {
      const list = res.data.results || res.data || [];
      setEntities(list);
      if (list.length && !entityId) setEntityId(list[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!entityId) return;
    financeAPI.periods.list({ entity: entityId }).then((res) => {
      const list = res.data.results || res.data || [];
      setPeriods(list);
      const open = list.find((p) => p.state === 'OPEN') || list[0];
      if (open) setPeriodName(open.name);
    }).catch(() => {});
  }, [entityId]);

  useEffect(() => {
    if (!entityId || !periodName) return;
    setLoading(true);
    setError(null);
    financeAPI.dashboard(entityId, periodName)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.error || 'Could not load the dashboard.'))
      .finally(() => setLoading(false));
  }, [entityId, periodName]);

  const scaling = data?.cost_of_time?.scaling_money_over_effort;
  const scalingLabel = useMemo(() => {
    if (scaling === null || scaling === undefined) return null;
    const ratio = parseFloat(scaling);
    if (ratio > 1) return 'Revenue is outpacing effort — scaling money, not just hours.';
    if (ratio > 0) return 'Growing, but roughly in step with the extra hours put in.';
    return 'Effort is up but revenue growth has not kept pace.';
  }, [scaling]);

  const fxDelta = data?.cash_position?.unrealized_fx_gain_loss_usd;
  const fxDeltaLabel = useMemo(() => {
    if (fxDelta === null || fxDelta === undefined) return 'actual liquidity, not paper profit';
    const amount = parseFloat(fxDelta);
    if (amount === 0) return 'actual liquidity, not paper profit — no FX movement since recording';
    const direction = amount > 0 ? 'gain' : 'loss';
    return `includes ${fs.money(Math.abs(amount), 'USD')} unrealized FX ${direction} vs. transaction-date rates`;
  }, [fxDelta]);

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiBarChart2 size={20} />CFO Metrics Dashboard</h1>
          <p style={fs.pageSubtitle}>ROI, cash flow, CAC, LTV and cost of time, with rollups across every reporting window.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} style={fs.select}>
            {entities.map((ent) => <option key={ent.id} value={ent.id}>{ent.name}</option>)}
          </select>
          <select value={periodName} onChange={(e) => setPeriodName(e.target.value)} style={fs.select}>
            {periods.map((p) => <option key={p.id} value={p.name}>{p.name} ({p.state})</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}><FiAlertTriangle size={18} /><span>{error}</span></div>
      )}

      {loading && <div style={fs.loadingState}>Loading metrics…</div>}

      {!loading && data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <StatCard icon={<FiTarget size={14} />} label="CAC" color={fs.colors.blue}
              value={data.cac?.cac_usd != null ? fs.money(data.cac.cac_usd, 'USD') : '—'}
              sub={`${data.cac?.new_enrolled_count ?? 0} new enrollments this period`} />
            <StatCard icon={<FiUsers size={14} />} label="LTV" color={fs.colors.purple}
              value={data.ltv?.ltv_usd != null ? fs.money(data.ltv.ltv_usd, 'USD') : '—'}
              sub={`${data.ltv?.enrolled_student_count ?? 0} enrolled students overall`} />
            <StatCard icon={<FiTrendingUp size={14} />} label="ROI (this period)" color={fs.colors.green}
              value={data.roi_overall?.roi != null ? percent(data.roi_overall.roi) : '—'}
              sub={data.roi_overall ? `revenue ${fs.money(data.roi_overall.revenue_usd, 'USD')} · costs ${fs.money(data.roi_overall.costs_usd, 'USD')}` : null} />
            <StatCard icon={<FiDollarSign size={14} />} label="Cash Position" color={fs.colors.amber}
              value={data.cash_position?.total_cash_usd != null ? fs.money(data.cash_position.total_cash_usd, 'USD') : '—'}
              sub={fxDeltaLabel} />
            <StatCard icon={<FiCloud size={14} />} label="Sheet Sync" color={data.pending_sheet_syncs ? fs.colors.amber : fs.colors.green}
              value={data.pending_sheet_syncs != null ? data.pending_sheet_syncs : '—'}
              sub={data.pending_sheet_syncs ? 'entries waiting to reach the backup spreadsheet' : 'all caught up'} />
          </div>

          {data.cash_position?.revaluation_incomplete && (
            <div style={{ ...fs.errorBanner, background: 'rgba(245,166,35,0.08)', borderColor: 'rgba(245,166,35,0.3)', color: fs.colors.amber }}>
              <FiAlertTriangle size={18} />
              <span>At least one currency has no current FX rate on record — its balance is excluded from Cash Position above, not guessed at.</span>
            </div>
          )}

          <div style={{ ...fs.card, marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: fs.colors.textBright, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiClock size={14} />Cost of time — are we scaling money or just effort?
            </div>
            {data.cost_of_time ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: fs.colors.textFaint, textTransform: 'uppercase' }}>Revenue growth</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: fs.colors.textBright }}>{percent(data.cost_of_time.revenue_growth)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: fs.colors.textFaint, textTransform: 'uppercase' }}>Effort growth</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: fs.colors.textBright }}>{percent(data.cost_of_time.effort_growth)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: fs.colors.textFaint, textTransform: 'uppercase' }}>Active hours (current vs previous)</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: fs.colors.textBright }}>
                    {data.cost_of_time.current_active_hours}h vs {data.cost_of_time.previous_active_hours}h
                  </div>
                </div>
                {scalingLabel && (
                  <div style={{ fontSize: 13, color: fs.colors.textDim, maxWidth: 320 }}>{scalingLabel}</div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: fs.colors.textFaint }}>No prior period to compare against yet.</div>
            )}
          </div>

          {!!data.cash_position?.by_holder?.length && (
            <div style={{ ...fs.card, marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: fs.colors.textBright, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiActivity size={14} />Cash by holder (as of {data.cash_position.as_of})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                {data.cash_position.by_holder.map((holder) => (
                  <div key={holder.account_code}>
                    <div style={{ fontSize: 11, color: fs.colors.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {holder.account_name}{holder.revaluation_incomplete && <span style={{ color: fs.colors.amber }}> ⚠</span>}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: fs.colors.textBright }}>{fs.money(holder.balance_usd, 'USD')}</div>
                    {holder.balance_usd_at_transaction_rates != null && parseFloat(holder.balance_usd_at_transaction_rates) !== parseFloat(holder.balance_usd) && (
                      <div style={{ fontSize: 11, color: fs.colors.textFaint }}>at transaction rates: {fs.money(holder.balance_usd_at_transaction_rates, 'USD')}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={fs.tableWrap}>
            <table style={fs.table}>
              <thead>
                <tr style={fs.tableHeadRow}>
                  <th style={fs.th}>Window</th>
                  <th style={fs.th}>Period</th>
                  <th style={fs.th}>Revenue</th>
                  <th style={fs.th}>Expenses</th>
                  <th style={fs.th}>Net</th>
                </tr>
              </thead>
              <tbody>
                {GRANULARITY_ROWS.map(([key, title]) => {
                  const row = data.rollups?.[key];
                  if (!row) return null;
                  return (
                    <tr key={key} style={{ borderBottom: `1px solid ${fs.colors.cardBorder}` }}>
                      <td style={{ ...fs.td, fontWeight: 600, color: fs.colors.textBright }}>{title}</td>
                      <td style={fs.td}>{row.label}</td>
                      <td style={fs.td}>{fs.money(row.revenue_usd, 'USD')}</td>
                      <td style={fs.td}>{fs.money(row.expense_usd, 'USD')}</td>
                      <td style={{ ...fs.td, color: parseFloat(row.net_usd) >= 0 ? fs.colors.green : fs.colors.red, fontWeight: 600 }}>
                        {fs.money(row.net_usd, 'USD')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CFOMetricsDashboard;
